import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ReactiveFormsModule, FormBuilder, FormGroup} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {MedicationService} from '@/services/medication.service';
import {FarmEnvironmentService} from '@/services/farm-environment.service';
import {PlacementService} from '@/services/placement.service';
import {ToastrService} from 'ngx-toastr';
import {
    SearchFilterComponent,
    FilterConfig
} from '@/components/search-filter/search-filter.component';
import {InsightsPanelComponent} from '@components/medication/insights-panel/insights-panel.component';
import {InfoTileComponent} from '@components/info-tile/info-tile.component';

@Component({
    selector: 'app-medication-panel',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        SearchFilterComponent,
        InfoTileComponent,
        InsightsPanelComponent
    ],
    templateUrl: './medication-panel.component.html',
    styleUrl: './medication-panel.component.scss'
})
export class MedicationPanelComponent implements OnInit {
    public userDetail: any;
    public filterForm: FormGroup;
    public medicationListFilterForm: FormGroup;
    public filteringData: any = null;

    public totalMedications: number = 0;
    public totalStock: number = 0;
    public expiringSoon: number = 0;
    public outOfStock: number = 0;
    public needToBeOrdered: number = 0;
    public farmOptions: any[] = [];
    public houseOptions: any[] = [];
    public partitionOptions: any[] = [];
    public cycleOptions: any[] = [];

    public medicationFilterConfig: FilterConfig = {
        showFarmFilter: false,
        showHouseFilter: false,
        fields: [
            {
                key: 'farmId',
                label: 'Farm',
                type: 'select',
                options: []
            },
            {
                key: 'houseId',
                label: 'House',
                type: 'select',
                options: []
            },
            {
                key: 'partitionId',
                label: 'Partition',
                type: 'select',
                options: []
            },
            {
                key: 'cycleId',
                label: 'Cycle',
                type: 'select',
                options: []
            }
        ]
    };

    constructor(
        private medicationService: MedicationService,
        private farmEnvironmentService: FarmEnvironmentService,
        private placementService: PlacementService,
        private toastr: ToastrService,
        private fb: FormBuilder
    ) {}

    ngOnInit() {
        this.userDetail = JSON.parse(
            localStorage.getItem('UserDetail') || '{}'
        );
        this.initializeForms();
        this.setupCascadingFilters();
        this.loadFarmHierarchy();
        this.loadCycles();
        this.loadMedicationStats();
    }

    // Load farm hierarchy using cascade approach
    private setupCascadingFilters(): void {
        // Listen for farm changes
        this.filterForm.get('farmId')?.valueChanges.subscribe((farmId) => {
            // When farm changes, reset house and partition
            this.filterForm.get('houseId')?.reset(null, {emitEvent: false});
            this.filterForm.get('partitionId')?.reset(null, {emitEvent: false});
            this.filterForm.get('cycleId')?.reset(null, {emitEvent: false});
            this.houseOptions = [];
            this.partitionOptions = [];

            const houseField = this.medicationFilterConfig.fields.find(
                (f) => f.key === 'houseId'
            );
            if (houseField) houseField.options = [];

            const partitionField = this.medicationFilterConfig.fields.find(
                (f) => f.key === 'partitionId'
            );
            if (partitionField) partitionField.options = [];

            if (farmId) {
                this.loadHousesForFarm(farmId);
            }

            // Reload cycles and stats when farm changes
            this.loadCycles();
            this.loadMedicationStats();
        });

        // Listen for house changes
        this.filterForm.get('houseId')?.valueChanges.subscribe((houseId) => {
            // When house changes, reset partition
            this.filterForm.get('partitionId')?.reset(null, {emitEvent: false});
            this.filterForm.get('cycleId')?.reset(null, {emitEvent: false});
            this.partitionOptions = [];

            const partitionField = this.medicationFilterConfig.fields.find(
                (f) => f.key === 'partitionId'
            );
            if (partitionField) partitionField.options = [];

            if (houseId) {
                this.loadPartitionsForHouse(houseId);
            }

            // Reload cycles when house changes
            this.loadCycles();
        });

        // Listen for partition changes
        this.filterForm
            .get('partitionId')
            ?.valueChanges.subscribe((partitionId) => {
                this.filterForm.get('cycleId')?.reset(null, {emitEvent: false});
                this.loadCycles();
            });
    }

    private loadFarmHierarchy() {
        if (this.userDetail?.id) {
            this.farmEnvironmentService
                .getdashboardFarmList(this.userDetail.id)
                .subscribe((result) => {
                    const farmData = Array.isArray(result)
                        ? result
                        : result.farms || result || [];
                    this.buildFarmOptions(farmData);
                });
        }
    }

    // Build farm options from the API data
    private buildFarmOptions(farmData: any[]) {
        this.farmOptions = farmData.map((farm) => ({
            value: farm.id,
            label: farm.name
        }));

        const farmField = this.medicationFilterConfig.fields.find(
            (f) => f.key === 'farmId'
        );
        if (farmField) {
            farmField.options = this.farmOptions;
        }

        // Auto-select the first farm if available
        if (this.farmOptions.length > 0) {
            const firstFarmId = this.farmOptions[0].value;
            this.filterForm.patchValue({farmId: firstFarmId});

            // Load houses for the selected farm and load stats
            this.loadHousesForFarm(firstFarmId);
            this.loadMedicationStats();
        }
    }

    private loadHousesForFarm(farmId: number) {
        this.farmEnvironmentService
            .getdashboardFarmHouseList(farmId)
            .subscribe((result) => {
                const houseData = Array.isArray(result)
                    ? result
                    : result.houses || result.farmHouses || result || [];
                this.houseOptions = houseData.map((house: any) => ({
                    value: house.id,
                    label: house.name
                }));

                // Update house options in filter config
                const houseField = this.medicationFilterConfig.fields.find(
                    (f) => f.key === 'houseId'
                );
                if (houseField) {
                    houseField.options = this.houseOptions;
                }
            });
    }

    // Load cycles from API
    private loadCycles() {
        const filterParams = {
            userId: this.userDetail.id,
            flockNo: '',
            placmentDate: null,
            farmId: this.filterForm.get('farmId')?.value || 0,
            farmHouseId: this.filterForm.get('houseId')?.value || 0,
            partitionId: this.filterForm.get('partitionId')?.value || 0
        };

        this.placementService.getPlacementList(filterParams).subscribe({
            next: (result) => {
                // Handle both array response and object response
                const placements = Array.isArray(result)
                    ? result
                    : result.placements || result || [];
                this.cycleOptions = placements.map((placement: any) => ({
                    value: placement.id,
                    label: `${placement.flockNo}`
                }));

                const cycleField = this.medicationFilterConfig.fields.find(
                    (f) => f.key === 'cycleId'
                );
                if (cycleField) {
                    cycleField.options = this.cycleOptions;
                }
            },
            error: (error) => {
                this.toastr.error('Error loading cycles');
                this.cycleOptions = [];
                const cycleField = this.medicationFilterConfig.fields.find(
                    (f) => f.key === 'cycleId'
                );
                if (cycleField) {
                    cycleField.options = [];
                }
            }
        });
    }

    private loadPartitionsForHouse(houseId: number) {
        this.farmEnvironmentService
            .getdashboardFarmHousePartitionList(houseId)
            .subscribe((result) => {
                const partitionData = Array.isArray(result)
                    ? result
                    : result.partitions ||
                      result.farmHousePartitions ||
                      result ||
                      [];
                this.partitionOptions = partitionData.map((partition: any) => ({
                    value: partition.id,
                    label: partition.name
                }));

                // Update partition options in filter config
                const partitionField = this.medicationFilterConfig.fields.find(
                    (f) => f.key === 'partitionId'
                );
                if (partitionField) {
                    partitionField.options = this.partitionOptions;
                }
            });
    }

    initializeForms() {
        this.filterForm = this.fb.group({
            farmId: [null],
            houseId: [null],
            partitionId: [null],
            cycleId: [null]
        });

        this.medicationListFilterForm = this.fb.group({
            name: [''],
            supplierId: [0],
            expiryDate: [null]
        });
    }

    // Load medication statistics from API
    loadMedicationStats() {
        // Get selected farm ID from the filter form
        const farmId = this.filterForm.get('farmId')?.value;

        // If no farm is selected, check if we have farms available and select the first one
        if (!farmId) {
            if (this.farmOptions.length > 0) {
                const firstFarmId = this.farmOptions[0].value;
                this.filterForm.patchValue({farmId: firstFarmId});
                this.loadMedicationStats();
                return;
            } else {
                // No farms available, reset to zero values
                this.totalMedications = 0;
                this.totalStock = 0;
                this.expiringSoon = 0;
                this.outOfStock = 0;
                this.needToBeOrdered = 0;
                return;
            }
        }

        this.medicationService.GetMedicationDataNumbers(farmId).subscribe({
            next: (response) => {
                this.totalMedications = response.totalMedications || 0;
                this.totalStock = response.totalStock || 0;
                this.expiringSoon = response.expiringSoon || 0;
                this.outOfStock = response.outofStock || 0;
                this.needToBeOrdered = response.needToBeOrdered || 0;
            },
            error: (error) => {
                console.error('Error loading medication stats:', error);
                this.toastr.error('Failed to load medication statistics');
                // Fallback to zero values on error
                this.totalMedications = 0;
                this.totalStock = 0;
                this.expiringSoon = 0;
                this.outOfStock = 0;
                this.needToBeOrdered = 0;
            }
        });
    }

    onMedicationFilterApplied(filterValues: any) {
        this.filteringData = this.filterForm.value;
        this.loadMedicationStats();
    }

    onMedicationFilterCleared() {
        this.filterForm.reset({
            farmId: null,
            houseId: null,
            partitionId: null,
            cycleId: null
        });
        this.medicationListFilterForm.reset();
        this.filteringData = null;

        // Clear dependent dropdowns
        this.houseOptions = [];
        this.partitionOptions = [];

        // Update filter config to clear house and partition options
        const houseField = this.medicationFilterConfig.fields.find(
            (f) => f.key === 'houseId'
        );
        if (houseField) {
            houseField.options = [];
        }

        const partitionField = this.medicationFilterConfig.fields.find(
            (f) => f.key === 'partitionId'
        );
        if (partitionField) {
            partitionField.options = [];
        }

        this.loadMedicationStats();
    }
}
